import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { DeleteIcon, ListIcon } from 'assets/icons';
import { PrimaryButton, SecondaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import CostTierBadge from 'components/UI/CostTierBadge';
import { useFeedback } from 'contexts/FeedbackContext';
import useIsAdmin from 'hooks/useIsAdmin';
import PromptEditor from 'pages/BusinessUnitConfig/components/PromptEditor';
import NewPromptSectionButton from 'pages/PromptSections/components/NewPromptSectionButton';
import PromptSectionModal, { type PromptSectionSaveOptions } from 'pages/PromptSections/components/PromptSectionModal';
import PromptSectionsForBusinessUnit from 'pages/PromptSections/components/PromptSectionsForBusinessUnit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useConfigureBusinessUnitSmtpEmailMutation,
  useDeleteAdminBusinessUnitDocumentMutation,
  useDeleteAdminBusinessUnitImageMutation,
  useDeleteAdminBusinessUnitVideoMutation,
  useEditAdminBusinessUnitMutation,
  useGetAdminAvailableFunctionsQuery,
  useGetAdminBusinessUnitQuery,
  useUploadAdminBusinessUnitDocumentsMutation,
  useUploadAdminBusinessUnitImagesMutation,
  useUploadAdminBusinessUnitVideosMutation,
} from 'services/api/adminBusinessUnits.api';
import {
  useCreateBusinessUnitPromptVersionMutation,
  useGetBusinessUnitPromptVersionsQuery,
} from 'services/api/businessUnitPromptVersions.api';
import { useLazyGetBusinessEmailConnectUrlQuery } from 'services/api/googleWorkspace.api';
import { useGetUniqueValuesOfLeadQuery } from 'services/api/leads.api';
import { useCreatePromptSectionMutation, useGetPromptSectionsQuery } from 'services/api/promptSections.api';
import type { BusinessUnitDraft, BusinessUnitUpdate } from 'services/types/businessUnit';
import type { BusinessUnitPromptVersion } from 'services/types/businessUnitPromptVersion';
import type { PromptSection, PromptSectionDraft } from 'services/types/promptSection';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
} from 'styles/tokens';
import { DEFAULT_AI_MODEL } from 'utils/aiModels';
import { getModelGroups } from 'utils/modelGroups';
import { getMutationErrorMessage } from 'utils/mutationError';

import styles from './BusinessUnitConfig.module.scss';
import ActivateSavedPromptVersionDialog from './components/ActivateSavedPromptVersionDialog';
import BusinessUnitAds from './components/BusinessUnitAds';
import BusinessUnitConfigHeader from './components/BusinessUnitConfigHeader';
import BusinessUnitTemplates from './components/BusinessUnitTemplates';
import BusinessUnitAlerts from './components/businessUnitAlerts';
import HolidaysSection from './components/HolidaysSection';
import PromptSectionPickerDialog from './components/PromptSectionPickerDialog';
import PromptTagBuilderDialog from './components/PromptTagBuilderDialog';
import PromptVersionHistoryDialog from './components/PromptVersionHistoryDialog';
import { analyzePromptTags, PROMPT_CONDITIONAL_TAG_COLORS, promptTagColor } from './components/promptTags';
import StatusConfigSection from './components/StatusConfigSection';
import TopicsConfigSection from './components/TopicsConfigSection';

const PROMPT_HELP_TEXT = (
  <Box
    sx={{
      mt: 2,
    }}
  >
    <Typography variant="h4">Instrucciones para el prompt</Typography>
    Dentro del prompt se pueden usar las siguientes variables:
    <br />
    <b>{'<CLIENT:attribute>'}</b>: Incluir un atributo del cliente. (Ej: {'<CLIENT:Rut>'})
    <br />
    <b>{'<CURRENT_DATE>'}</b>: Fecha actual
    <br />
    <b>{'<UF_PRICE>'}</b>: Valor actual de la UF
    <br />
    <b>{'<IF:attr>Texto</IF:attr>'}</b>: Incluye el &quot;Texto&quot; solo si existe el atributo
    <br />
    <b>{'<IF:!attr>Texto</IF:!attr>'}</b>: Incluye el &quot;Texto&quot; solo si el atributo no es válido, es falso o es
    cero.
    <br />
    <b>{'<IF:attr_operador_valor>Texto</IF:attr_operador_valor>'}</b>: Incluye el &quot;Texto&quot; solo si el atributo
    cumple la comparación.
    <br />
    <b>{'<SECTION:identifier>'}</b>: Incluye una sección de prompt. Puedes ver las disponibles en &quot;Ver secciones de
    prompt&quot;
    <br />
    <b>{'<WORKING_HOURS>Texto</WORKING_HOURS>'}</b>: Incluye el &quot;Texto&quot; solo si estamos en horario laboral
    <br />
    <b>{'<OUT_OF_WORKING_HOURS>Texto</OUT_OF_WORKING_HOURS>'}</b>: Incluye el &quot;Texto&quot; solo si estamos fuera de
    horario laboral
    <br />
    <Box
      sx={{
        padding: '20px',
      }}
    >
      En <b>attr</b> puede ir <i>rut</i>, <i>email</i>, <i>salary</i>, <i>savingsAmount</i>, etc
      <br />
      En <b>operador</b> puede ir <i>=</i> (igual), <i>!=</i> (distinto), <i>&gt;</i> (mayor), <i>&lt;</i>, <i>&gt;=</i>
      , <i>&lt;=</i>, <i>startsWith</i>, <i>endsWith</i>, <i>contains</i>, <i>!contains</i> (no contiene), <i>in</i>{' '}
      (está dentro de una lista separada por comas)
      <br />
      En <b>valor</b> puede ir un número, un string, un true (Si) o un false (No)
      <br />
      Para atributos que son listas (como <i>topics</i>), usa <i>contains</i> / <i>!contains</i> (o sus equivalentes{' '}
      <i>=</i> / <i>!=</i>) para saber si la lista incluye o no un elemento, o <i>in</i> para saber si algún elemento de
      la lista está dentro de una lista de valores separados por comas. Una lista vacía no contiene nada.
      <br />
      Ejemplos:
      <ul>
        <li>{'<IF:email_contains_@salfa>Este es un empleado de Salfa, trátalo bien</IF:email_contains_@salfa>'}</li>
        <li>{'<IF:messagesCount_>_4><IF:!rut>Debes pedir el rut</IF:!rut></IF:messagesCount_>_4>'}</li>
        <li>
          {
            '<IF:nationality><IF:nationality_!=_CHILENA>Pregunta si tiene residencia definitiva</IF:nationality_!=_CHILENA></IF:nationality>'
          }
        </li>
        <li>
          {
            '<IF:clientMessagesCount_>_5>Es un cliente interesado, guíalo a visitar la sucursal</IF:clientMessagesCount_>_5>'
          }
        </li>
        <li>{'<IF:imagesSent_=_0>Pregúntale si quiere fotos</IF:imagesSent_=_0>'}</li>
        <li>{'<IF:imagesSent_>_0>Pregúntale si quiere más fotos</IF:imagesSent_>_0>'}</li>
        <li>{'<IF:floidRequestsCount_>_0>Tiene 1+ floid requests</IF:floidRequestsCount_>_0>'}</li>
        <li>{'<IF:topics_contains_posventa>El cliente tiene el topic posventa</IF:topics_contains_posventa>'}</li>
        <li>{'<IF:topics_!contains_posventa>El cliente no tiene el topic posventa</IF:topics_!contains_posventa>'}</li>
        <li>
          {
            '<IF:topics_in_posventa,reclamo>El cliente tiene al menos uno de los topics posventa o reclamo</IF:topics_in_posventa,reclamo>'
          }
        </li>
      </ul>
      En attr se puede poner uno de los siguientes atributos (debe ser exacto, considerando las mayúsculas y minúsculas,
      no lo ocupes si no sabes qué significa):
      <ul>
        <li>email</li>
        <li>rut</li>
        <li>phone</li>
        <li>firstName</li>
        <li>lastName</li>
        <li>motherLastName</li>
        <li>savingsAmount</li>
        <li>notes</li>
        <li>adminNotes</li>
        <li>dateOfBirth</li>
        <li>gender</li>
        <li>nationality</li>
        <li>maritalStatus</li>
        <li>salary</li>
        <li>salarySupplement</li>
        <li>score</li>
        <li>lastMessageDate</li>
        <li>messagesCount</li>
        <li>imagesSent</li>
        <li>followUpsSent</li>
        <li>floidRequestsCount</li>
        <li>floidRequestsDetails</li>
        <li>isTestClient</li>
        <li>disabledAutomaticResponses</li>
        <li>source</li>
        <li>status</li>
        <li>userScore</li>
        <li>financialScore</li>
        <li>interestScore</li>
        <li>manualScore</li>
        <li>prospectMessage</li>
        <li>aiSafetyStatus</li>
        <li>topics (lista; usar con contains / !contains / = / != / in)</li>
      </ul>
      <a
        href="https://www.loom.com/share/c9e2f8008df44b82bf04eedf9ff95bd8?sid=f4404379-26c6-4421-8693-68f75a62b08e"
        target="_blank"
        rel="noreferrer"
      >
        Video instrucciones 1
      </a>
      <br />
      <a
        href="https://www.loom.com/share/8ca6921e97c74631bcee788ad8d1ca6c?sid=d04e35a1-3c09-45c1-a482-6fde8cda3397"
        target="_blank"
        rel="noreferrer"
      >
        Video instrucciones 2
      </a>
      <br />
      <a
        href="https://www.loom.com/share/8ff296ef858141d08c00654b635106f7?sid=ace50e83-e46e-407d-9a2b-335abd079198"
        target="_blank"
        rel="noreferrer"
      >
        Video instrucciones 3
      </a>
    </Box>
  </Box>
);

const PROMPT_LINK_COLOR = '#0072B2';

const DEPTH_MARK_OPEN = '​';
const DEPTH_MARK_CLOSE = '‌';
// `(!?)` flags an invalid (unmatched / orphan) tag so the renderer can paint it red.
const PROMPT_INLINE_RE = new RegExp(
  `${DEPTH_MARK_OPEN}(\\d)(!?)${DEPTH_MARK_CLOSE}(<[^<>]+>)|(https?:\\/\\/\\S+)`,
  'g'
);

const renderPromptInline = (text: string, baseKey: string): React.ReactNode => {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let i = 0;
  for (const match of text.matchAll(PROMPT_INLINE_RE)) {
    const matchStart = match.index ?? 0;
    if (matchStart > cursor) out.push(text.substring(cursor, matchStart));
    const tag = match[3];
    const url = match[4];
    if (tag) {
      const depthDigit = Number.parseInt(match[1], 10);
      const isInvalid = match[2] === '!';
      const color = promptTagColor(tag, depthDigit, isInvalid);
      const colon = tag.indexOf(':');
      const keyword = colon >= 0 ? tag.slice(0, colon) : tag;
      const rest = colon >= 0 ? tag.slice(colon) : '';
      const sectionName = tag.match(/^<SECTION:([^>]+)>$/)?.[1];
      out.push(
        <Box
          key={`${baseKey}-${i}`}
          component="span"
          data-prompt-section-name={sectionName}
          sx={{
            color,
            cursor: sectionName ? 'pointer' : 'text',
            fontFamily: 'monospace',
            fontWeight: 'normal',
            fontSize: '0.92em',
            textDecoration: sectionName ? 'underline dotted' : 'none',
          }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {keyword}
          </Box>
          {rest}
        </Box>
      );
    } else if (url) {
      out.push(
        <Box
          key={`${baseKey}-${i}`}
          component="a"
          href={url}
          target="_blank"
          rel="noreferrer"
          sx={{ color: PROMPT_LINK_COLOR, textDecoration: 'underline' }}
        >
          {url}
        </Box>
      );
    }
    cursor = matchStart + match[0].length;
    i++;
  }
  if (cursor < text.length) out.push(text.substring(cursor));
  return out;
};

const transformPromptChildren = (children: React.ReactNode): React.ReactNode =>
  React.Children.map(children, (child, idx) => {
    if (typeof child === 'string') return renderPromptInline(child, `t${idx}`);
    return child;
  });

const promptMdComponents: Components = {
  h1: ({ children }) => (
    <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 2, mb: 1 }}>
      {transformPromptChildren(children)}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 2, mb: 0.5 }}>
      {transformPromptChildren(children)}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="subtitle1"
      sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif', mt: 1.5, mb: 0.5 }}
    >
      {transformPromptChildren(children)}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body2" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif', lineHeight: 1.7, mb: 1 }}>
      {transformPromptChildren(children)}
    </Typography>
  ),
  li: ({ children }) => (
    <li>
      <Typography
        variant="body2"
        component="span"
        sx={{ color: NAVY, fontFamily: 'Jost, sans-serif', lineHeight: 1.7 }}
      >
        {transformPromptChildren(children)}
      </Typography>
    </li>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600, color: NAVY }}>
      {transformPromptChildren(children)}
    </Box>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: SURFACE_TINT,
            borderRadius: RADIUS_ITEM,
            p: 1.5,
            my: 1,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: NAVY,
          }}
        >
          <code>{children}</code>
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          bgcolor: SURFACE_TINT,
          borderRadius: '4px',
          px: 0.5,
          py: 0.25,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          color: NAVY,
        }}
      >
        {children}
      </Box>
    );
  },
};

// A line made up solely of tags (e.g. `</IF:x>` or `</IF_LEAD:a></IF:b>`).
const TAG_ONLY_LINE_RE = /^\s*(?:<[^<>]+>\s*)+$/;
// A numbered / bulleted outline line (`1. `, `2.1. `, `3.2.1. `, `- `).
const OUTLINE_LINE_RE = /^\s*(?:\d+(?:\.\d+)*[.)]|[-*+])\s/;

const listActiveTowards = (lines: string[], start: number, dir: -1 | 1): boolean => {
  for (let j = start + dir; j >= 0 && j < lines.length; j += dir) {
    if (lines[j].trim() === '') return false;
    if (OUTLINE_LINE_RE.test(lines[j])) return true;
  }
  return false;
};

const isolateTagLines = (raw: string): string => {
  const lines = raw.split('\n');
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const inBetween = listActiveTowards(lines, i, -1) && listActiveTowards(lines, i, 1);
    const isBoundaryTag = TAG_ONLY_LINE_RE.test(line) && !inBetween;
    if (isBoundaryTag) {
      if (out.length > 0 && out[out.length - 1].trim() !== '') out.push('');
      out.push(line);
      if (i + 1 < lines.length && lines[i + 1].trim() !== '') out.push('');
    } else {
      out.push(line);
    }
  }
  return out.join('\n');
};

// Pre-process the prompt for Markdown rendering. For each tag in source order we
// embed a zero-width depth marker (e.g. ​0‌<TAG>) so the renderer can colour the
// tag without a stateful iterator. Brackets are then escaped so <TAG> shows up as
// literal text, and single newlines become hard line breaks.
const annotatePromptForMarkdown = (rawInput: string): string => {
  const raw = isolateTagLines(rawInput);
  const { depths, invalid } = analyzePromptTags(raw);
  const tags = Array.from(raw.matchAll(/<[^<>]+>/g));
  let out = '';
  let cursor = 0;
  for (let i = 0; i < tags.length; i++) {
    const match = tags[i];
    const tagStart = match.index ?? 0;
    out += raw.substring(cursor, tagStart);
    const digit = (depths[i] % PROMPT_CONDITIONAL_TAG_COLORS.length).toString();
    const flag = invalid[i] ? '!' : '';
    out += `${DEPTH_MARK_OPEN}${digit}${flag}${DEPTH_MARK_CLOSE}${match[0]}`;
    cursor = tagStart + match[0].length;
  }
  out += raw.substring(cursor);
  return out
    .replace(/</g, '\\<')
    .replace(/>/g, '\\>')
    .replace(/(?<! {2})\n(?!\n)/g, '  \n');
};

const AutocompletePaper = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <Paper
    {...props}
    sx={{
      mt: 1,
      boxShadow: SHADOW,
      borderRadius: RADIUS_CARD,
      backdropFilter: 'blur(16px)',
      backgroundColor: 'rgba(249, 250, 253, 0.95)',
      maxHeight: 400,
      '& .MuiAutocomplete-listbox': {
        maxHeight: 400,
        '& .MuiAutocomplete-option': {
          '&:not(:last-child)': {
            boxShadow: `inset 0 -1px 0 ${SURFACE_TINT}`,
          },
        },
      },
    }}
  />
);

const navyCheckboxSx = {
  color: 'rgba(21, 24, 58, 0.45)',
  '&.Mui-checked': { color: NAVY },
} as const;

const SectionCard: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      bgcolor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW,
      p: { xs: 2.5, lg: 3 },
      mb: 3,
    }}
  >
    {(title || action) && (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: subtitle ? 0.5 : 2 }}
      >
        {title && (
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
            {title}
          </Typography>
        )}
        {action}
      </Box>
    )}
    {subtitle && (
      <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: 'rgba(21, 24, 58, 0.6)', mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const DropdownCard: React.FC<{
  label: string;
  subtitle?: React.ReactNode;
  open: boolean;
  onChange: () => void;
  children?: React.ReactNode;
}> = ({ label, subtitle, open, onChange, children }) => (
  <Box
    sx={{
      bgcolor: SURFACE_CONTAINER_LOWEST,
      borderRadius: RADIUS_CARD,
      boxShadow: SHADOW,
      mb: 3,
      overflow: 'hidden',
    }}
  >
    <Box
      role="button"
      tabIndex={0}
      onClick={onChange}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        p: { xs: 2, lg: 2.5 },
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: SURFACE_HOVER },
      }}
    >
      <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
        {label}
      </Typography>
      <ExpandMoreIcon
        sx={{
          color: 'rgba(21, 24, 58, 0.6)',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 160ms ease',
        }}
      />
    </Box>
    <Collapse in={open} unmountOnExit>
      <Box sx={{ px: { xs: 2, lg: 2.5 }, pb: { xs: 2, lg: 2.5 } }}>
        {subtitle && (
          <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: 'rgba(21, 24, 58, 0.6)', mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </Box>
    </Collapse>
  </Box>
);

// Generic SMTP/IMAP mailbox config (provider = 'smtp'). Gmail lives in its own card; this one wires a
// server we're given host/port/user/pass for. The password is never returned by the API, so an empty
// field on save keeps the stored one.
const SmtpEmailCard: React.FC<{
  businessUnitId: number;
  emailProperties?: BusinessUnitDraft['emailProperties'];
  onSaved: () => void;
}> = ({ businessUnitId, emailProperties, onSaved }) => {
  const [open, setOpen] = useState(false);
  const isSmtp = emailProperties?.provider === 'smtp';
  const [address, setAddress] = useState(isSmtp ? (emailProperties?.address ?? '') : '');
  const [smtpHost, setSmtpHost] = useState(emailProperties?.smtpHost ?? '');
  const [smtpPort, setSmtpPort] = useState(emailProperties?.smtpPort ? String(emailProperties.smtpPort) : '587');
  const [imapHost, setImapHost] = useState(emailProperties?.imapHost ?? '');
  const [imapPort, setImapPort] = useState(emailProperties?.imapPort ? String(emailProperties.imapPort) : '993');
  const [password, setPassword] = useState('');
  const [draftMode, setDraftMode] = useState(emailProperties?.draftMode ?? true);
  const [configureSmtp, { isLoading }] = useConfigureBusinessUnitSmtpEmailMutation();

  const handleSave = async () => {
    try {
      await configureSmtp({
        businessUnitId,
        address,
        smtpHost,
        smtpPort: Number(smtpPort),
        imapHost,
        imapPort: Number(imapPort),
        password: password || undefined,
        draftMode,
      }).unwrap();
      setPassword('');
      onSaved();
    } catch (error) {
      console.error('Error saving SMTP email config', error);
    }
  };

  const canSave = !!address && !!smtpHost && !!smtpPort && !!imapHost && !!imapPort && (isSmtp || !!password);

  return (
    <DropdownCard label="Casilla de email (SMTP/IMAP)" open={open} onChange={() => setOpen(!open)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
        <Typography variant="body2" sx={{ color: NAVY }}>
          {isSmtp
            ? `Configurada: ${emailProperties?.address}. El inbound se revisa cada ~1 minuto vía IMAP.`
            : 'Conecta un servidor SMTP/IMAP (dominio, puerto, usuario y contraseña) para recibir y responder correos.'}
        </Typography>
        <TextField
          label="Email"
          size="small"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="arturo@siempreseguro.cl"
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            label="SMTP host"
            size="small"
            fullWidth
            value={smtpHost}
            onChange={(e) => setSmtpHost(e.target.value)}
            placeholder="mail.siempreseguro.cl"
          />
          <TextField
            label="SMTP port"
            size="small"
            type="number"
            sx={{ width: 120 }}
            value={smtpPort}
            onChange={(e) => setSmtpPort(e.target.value)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            label="IMAP host"
            size="small"
            fullWidth
            value={imapHost}
            onChange={(e) => setImapHost(e.target.value)}
            placeholder="mail.siempreseguro.cl"
          />
          <TextField
            label="IMAP port"
            size="small"
            type="number"
            sx={{ width: 120 }}
            value={imapPort}
            onChange={(e) => setImapPort(e.target.value)}
          />
        </Box>
        <TextField
          label={isSmtp ? 'Contraseña (deja vacío para mantener)' : 'Contraseña'}
          size="small"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <FormControlLabel
          control={<Checkbox checked={draftMode} onChange={(e) => setDraftMode(e.target.checked)} />}
          label="Modo borrador (la IA genera la respuesta pero no la envía; queda como descartada para revisión)"
        />
        <PrimaryButton disabled={isLoading || !canSave} onClick={handleSave}>
          {isSmtp ? 'Guardar cambios' : 'Conectar servidor'}
        </PrimaryButton>
      </Box>
    </DropdownCard>
  );
};

const BusinessUnits = () => {
  const { businessUnitId } = useParams();
  const numericBusinessUnitId = Number(businessUnitId);
  const { currentData: businessUnit, refetch } = useGetAdminBusinessUnitQuery(numericBusinessUnitId);
  const { data: promptSections, refetch: refetchPromptSections } = useGetPromptSectionsQuery({
    businessUnitId: numericBusinessUnitId,
  });
  const { data: reportSourceOptions } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: Number(businessUnitId), startDate: null, endDate: null, attribute: 'source' },
    { skip: !businessUnitId }
  );
  const [createPromptSection] = useCreatePromptSectionMutation();
  const [triggerEmailConnectUrl, { isFetching: isConnectingEmail }] = useLazyGetBusinessEmailConnectUrlQuery();
  const { data: availableFunctions = [] } = useGetAdminAvailableFunctionsQuery(undefined);
  // Only the active version id and the total matter here — the history dialog paginates on its own.
  const { currentData: promptVersionsPage, refetch: refetchPromptVersions } = useGetBusinessUnitPromptVersionsQuery({
    businessUnitId: numericBusinessUnitId,
    limit: 1,
  });
  const [prompt, setPrompt] = useState<string>('');
  const [versionBaselinePrompt, setVersionBaselinePrompt] = useState<string>('');
  const [versionBaselineFunctions, setVersionBaselineFunctions] = useState<string[]>([]);
  const [editorParentVersionId, setEditorParentVersionId] = useState<number | null>(null);
  const [versionNote, setVersionNote] = useState('');
  const [versionError, setVersionError] = useState<string | null>(null);
  const [showPromptVersionHistory, setShowPromptVersionHistory] = useState(false);
  const [compareUnsavedPrompt, setCompareUnsavedPrompt] = useState(false);
  const [savedPromptVersion, setSavedPromptVersion] = useState<BusinessUnitPromptVersion | null>(null);
  const initializedPromptBusinessUnitIdRef = useRef<number | null>(null);
  const [refetchFlag, setRefetchFlag] = useState<boolean>(false);
  const [refetchPromptSectionsFlag, setRefetchPromptSectionsFlag] = useState<number>(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [updateBusinessUnit, { isError, error, isLoading: isUpdatingBusinessUnit }] =
    useEditAdminBusinessUnitMutation();
  const [createPromptVersion, { isLoading: isSavingPromptVersion }] = useCreateBusinessUnitPromptVersionMutation();
  const [deleteBusinessUnitImage] = useDeleteAdminBusinessUnitImageMutation();
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [tagBuilderOpen, setTagBuilderOpen] = useState(false);
  const [sectionPickerOpen, setSectionPickerOpen] = useState(false);
  const [sectionCreatorOpen, setSectionCreatorOpen] = useState(false);
  const [sectionCreatorValue, setSectionCreatorValue] = useState('');
  const [hasPromptSelection, setHasPromptSelection] = useState(false);
  const [editingPromptSection, setEditingPromptSection] = useState<PromptSection | null>(null);
  const availablePromptSections = useMemo(
    () =>
      (promptSections ?? [])
        .filter((section: PromptSection) => section.isLastVersion && !section.isSuggestion)
        .sort((a: PromptSection, b: PromptSection) => a.displayName.localeCompare(b.displayName))
        .map((section: PromptSection) => ({ name: section.name, displayName: section.displayName })),
    [promptSections]
  );
  const promptPreview = useMemo(
    // Annotating is a full pass over the prompt; while editing, the preview is hidden anyway.
    () =>
      isEditingPrompt ? null : <Markdown components={promptMdComponents}>{annotatePromptForMarkdown(prompt)}</Markdown>,
    [prompt, isEditingPrompt]
  );
  const tagInsertRangeRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const tagInitialInnerRef = useRef<string>('');
  const promptHeightRef = useRef<number>(400);
  const promptScrollRatioRef = useRef<number>(0);
  const promptCleanupRef = useRef<(() => void) | null>(null);
  const promptContainerRef = useRef<HTMLDivElement | null>(null);
  const promptScrollBoxRef = useRef<HTMLElement | null>(null);
  const promptEditorRef = useRef<ReactCodeMirrorRef>(null);
  const attachPromptBox = useCallback((node: HTMLElement | null) => {
    promptCleanupRef.current?.();
    promptCleanupRef.current = null;
    promptScrollBoxRef.current = node;
    if (!node) return;
    node.style.height = `${promptHeightRef.current}px`;
    const observer = new ResizeObserver(() => {
      promptHeightRef.current = node.offsetHeight;
    });
    observer.observe(node);
    const max = Math.max(1, node.scrollHeight - node.clientHeight);
    if (max > 1) {
      node.scrollTop = promptScrollRatioRef.current * max;
    }
    const updateRatio = () => {
      const m = Math.max(1, node.scrollHeight - node.clientHeight);
      if (m > 1) promptScrollRatioRef.current = node.scrollTop / m;
    };
    node.addEventListener('scroll', updateRatio);
    promptCleanupRef.current = () => {
      observer.disconnect();
      node.removeEventListener('scroll', updateRatio);
    };
  }, []);
  // Carry the scroll position across the preview ⇄ edit toggle. The preview scrolls the
  // outer box; CodeMirror scrolls its own .cm-scroller. Sync both through a shared ratio
  // so entering/exiting edit mode doesn't jump back to the top.
  useEffect(() => {
    if (isEditingPrompt) {
      const ratio = promptScrollRatioRef.current;
      let scroller: HTMLElement | null = null;
      const onScroll = () => {
        if (!scroller) return;
        const max = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
        if (max > 1) promptScrollRatioRef.current = scroller.scrollTop / max;
      };
      let raf = requestAnimationFrame(function attach() {
        const view = promptEditorRef.current?.view;
        // Wait until CodeMirror has mounted and measured, otherwise scrollHeight is the
        // unmeasured (collapsed) height and the restore lands at the top.
        if (!view || view.scrollDOM.scrollHeight <= view.scrollDOM.clientHeight + 1) {
          raf = requestAnimationFrame(attach);
          return;
        }
        scroller = view.scrollDOM;
        // Focus first (which scrolls the caret into view), THEN restore the exact scroll
        // on the next frame — so the final position is wherever the user scrolled to,
        // never wherever the caret/selection happened to be.
        view.focus();
        raf = requestAnimationFrame(() => {
          if (!scroller) return;
          const max = Math.max(1, scroller.scrollHeight - scroller.clientHeight);
          scroller.scrollTop = ratio * max;
          scroller.addEventListener('scroll', onScroll);
        });
      });
      return () => {
        cancelAnimationFrame(raf);
        scroller?.removeEventListener('scroll', onScroll);
      };
    }
    // Exiting to preview: restore the outer box scroll from the ratio captured in edit.
    const box = promptScrollBoxRef.current;
    if (box) {
      const raf = requestAnimationFrame(() => {
        const max = Math.max(1, box.scrollHeight - box.clientHeight);
        box.scrollTop = promptScrollRatioRef.current * max;
      });
      return () => cancelAnimationFrame(raf);
    }
    return undefined;
  }, [isEditingPrompt]);
  useEffect(() => {
    if (!isEditingPrompt || tagBuilderOpen || sectionPickerOpen || sectionCreatorOpen || editingPromptSection) return;
    const handleMouseDown = (e: MouseEvent) => {
      const container = promptContainerRef.current;
      if (container && !container.contains(e.target as Node)) {
        setIsEditingPrompt(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isEditingPrompt, tagBuilderOpen, sectionPickerOpen, sectionCreatorOpen, editingPromptSection]);
  const [showStructuredPrompt, setShowStructuredPrompt] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [showBusinessUnitAds, setShowBusinessUnitAds] = useState(false);
  const [showLeadOrigin, setShowLeadOrigin] = useState(false);
  const [showHolidays, setShowHolidays] = useState(false);
  const [showEmailChannel, setShowEmailChannel] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [imagesToUpload, setImagesToUpload] = useState<File[] | null>(null);
  const [uploadBusinessUnitImages] = useUploadAdminBusinessUnitImagesMutation();
  const [videosToUpload, setVideosToUpload] = useState<File[] | null>(null);
  const [deleteBusinessUnitVideo] = useDeleteAdminBusinessUnitVideoMutation();
  const [uploadBusinessUnitVideos] = useUploadAdminBusinessUnitVideosMutation();
  const [documentsToUpload, setDocumentsToUpload] = useState<File[] | null>(null);
  const [deleteBusinessUnitDocument] = useDeleteAdminBusinessUnitDocumentMutation();
  const [uploadBusinessUnitDocuments] = useUploadAdminBusinessUnitDocumentsMutation();
  const [functions, setFunctions] = React.useState<string[]>([]);
  const { notifySaved } = useFeedback();
  const [selectedAIModel, setSelectedAIModel] = useState<string>(DEFAULT_AI_MODEL);
  const aiModelOptions = useMemo(
    () =>
      getModelGroups().flatMap((group) => [
        <ListSubheader
          key={`bu-header-${group.provider}`}
          disableSticky
          sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          {group.provider}
        </ListSubheader>,
        ...group.models.map((model) => (
          <MenuItem key={model} value={model} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
            <span>{model}</span>
            <CostTierBadge model={model} />
          </MenuItem>
        )),
      ]),
    []
  );
  const isUnknownAIModel = useMemo(
    () =>
      Boolean(selectedAIModel) &&
      selectedAIModel !== DEFAULT_AI_MODEL &&
      !getModelGroups().some((group) => (group.models as readonly string[]).includes(selectedAIModel)),
    [selectedAIModel]
  );
  const [daysToCloseLeads, setDaysToCloseLeads] = useState<number | null>(null);
  const [hasSellerManagement, setHasSellerManagement] = useState<boolean>(false);
  const [hasClientQueueTableActive, setHasClientQueueTableActive] = useState<boolean>(false);
  const [hasCallingEnabled, setHasCallingEnabled] = useState<boolean>(false);
  const [uploadSimulationsToCRM, setUploadSimulationsToCRM] = useState<boolean>(false);
  const [customReportInstructions, setCustomReportInstructions] = useState<string>('');
  const [reportSources, setReportSources] = useState<string[]>([]);
  const [websiteWhatsAppMessages, setWebsiteWhatsAppMessages] = useState<string[]>([]);
  const [websiteFormMessages, setWebsiteFormMessages] = useState<string[]>([]);
  const [mailingMessages, setMailingMessages] = useState<string[]>([]);
  const [tiktokMessages, setTiktokMessages] = useState<string[]>([]);
  const activePromptVersionId = promptVersionsPage?.activeVersionId ?? null;
  const promptVersionsTotal = promptVersionsPage?.total ?? 0;
  const normalizedFunctions = useMemo(() => [...new Set(functions)].sort((a, b) => a.localeCompare(b)), [functions]);
  const normalizedBaselineFunctions = useMemo(
    () => [...new Set(versionBaselineFunctions)].sort((a, b) => a.localeCompare(b)),
    [versionBaselineFunctions]
  );
  const areFunctionsDirty = JSON.stringify(normalizedFunctions) !== JSON.stringify(normalizedBaselineFunctions);
  const isPromptSnapshotDirty = prompt !== versionBaselinePrompt || areFunctionsDirty;
  const isPromptVersionDirty = isPromptSnapshotDirty || versionNote.trim().length > 0;

  const applyVersionToEditor = useCallback((version: BusinessUnitPromptVersion) => {
    const nextPrompt = version.prompt ?? '';
    const nextFunctions = version.availableFunctions ?? [];
    setPrompt(nextPrompt);
    setFunctions(nextFunctions);
    setVersionBaselinePrompt(nextPrompt);
    setVersionBaselineFunctions(nextFunctions);
    setEditorParentVersionId(version.id);
    setVersionNote('');
    setVersionError(null);
  }, []);

  const handleSavePromptVersion = async () => {
    if (!businessUnit) return;
    setVersionError(null);
    // The editor lifts its text on a debounce, so read the live document: saving must never
    // persist a stale snapshot of what is on screen.
    const livePrompt = promptEditorRef.current?.view?.state.doc.toString() ?? prompt;
    if ((livePrompt.match(/<CACHE_BREAKPOINT>/g) ?? []).length > 1) {
      setVersionError('El prompt no puede tener más de un marcador <CACHE_BREAKPOINT>.');
      return;
    }
    if (livePrompt === versionBaselinePrompt && !areFunctionsDirty) {
      setVersionError('No hay cambios para guardar como una nueva versión.');
      return;
    }
    try {
      const created = await createPromptVersion({
        businessUnitId: businessUnit.id,
        version: {
          prompt: livePrompt,
          availableFunctions: normalizedFunctions,
          note: versionNote.trim() || null,
          activate: false,
          parentVersionId: editorParentVersionId,
          expectedActiveVersionId: activePromptVersionId,
        },
      }).unwrap();
      applyVersionToEditor(created);
      setSavedPromptVersion(created);
      notifySaved('Versión guardada');
      await refetchPromptVersions();
    } catch (mutationError) {
      setVersionError(getMutationErrorMessage(mutationError, 'No fue posible guardar la versión.'));
    }
  };
  const capturePromptSelection = () => {
    const view = promptEditorRef.current?.view;
    if (view) {
      const { from, to } = view.state.selection.main;
      tagInsertRangeRef.current = { start: from, end: to };
      tagInitialInnerRef.current = to > from ? view.state.doc.sliceString(from, to) : '';
    } else {
      tagInsertRangeRef.current = { start: prompt.length, end: prompt.length };
      tagInitialInnerRef.current = '';
    }
  };

  const openTagBuilder = () => {
    capturePromptSelection();
    setTagBuilderOpen(true);
  };

  const openSectionPicker = () => {
    capturePromptSelection();
    setSectionPickerOpen(true);
  };

  const openSectionCreator = () => {
    capturePromptSelection();
    setSectionCreatorValue(tagInitialInnerRef.current);
    setSectionCreatorOpen(true);
  };

  const openPromptSectionEditor = useCallback(
    (sectionName: string) => {
      const section = promptSections?.find(
        (candidate: PromptSection) =>
          candidate.name === sectionName && candidate.isLastVersion && !candidate.isSuggestion
      );
      if (section) setEditingPromptSection(section);
    },
    [promptSections]
  );

  const handlePromptSelectionChange = useCallback((selectedText: string) => {
    setHasPromptSelection(selectedText.length > 0);
  }, []);

  const insertPromptTag = (tag: string) => {
    const { start, end } = tagInsertRangeRef.current;
    const view = promptEditorRef.current?.view;
    if (view) {
      const docLength = view.state.doc.length;
      const safeStart = Math.min(start, docLength);
      const safeEnd = Math.min(Math.max(end, safeStart), docLength);
      // Dispatch through CodeMirror so the insertion lands in its native undo stack
      // and onChange syncs `prompt`; place the caret right after the inserted tag.
      view.dispatch({
        changes: { from: safeStart, to: safeEnd, insert: tag },
        selection: { anchor: safeStart + tag.length },
        scrollIntoView: true,
      });
      view.focus();
    } else {
      const safeStart = Math.min(start, prompt.length);
      const safeEnd = Math.min(Math.max(end, safeStart), prompt.length);
      setPrompt(prompt.substring(0, safeStart) + tag + prompt.substring(safeEnd));
    }
    setTagBuilderOpen(false);
  };

  const createSectionFromPromptSelection = async (
    promptSection: PromptSectionDraft,
    options: PromptSectionSaveOptions
  ) => {
    if (!businessUnitId) return;
    const created = await createPromptSection({
      businessUnitId: Number(businessUnitId),
      promptSection,
      activatePromptVersion: options.activatePromptVersion,
    }).unwrap();
    insertPromptTag(`<SECTION:${created.name}>`);
    await refetchPromptSections();
    notifySaved('Sección creada');
    setSectionCreatorOpen(false);
  };

  const updatePromptSectionFromEditor = async (
    promptSection: PromptSectionDraft,
    options: PromptSectionSaveOptions
  ) => {
    if (!businessUnitId) return;
    await createPromptSection({
      businessUnitId: Number(businessUnitId),
      promptSection,
      activatePromptVersion: options.activatePromptVersion,
    }).unwrap();
    const { data: refreshedVersions } = await refetchPromptVersions();
    if (refreshedVersions?.activeVersionId) {
      setEditorParentVersionId(refreshedVersions.activeVersionId);
    }
    await refetchPromptSections();
    notifySaved(
      options.activatePromptVersion
        ? 'Sección actualizada'
        : 'Sección guardada en una versión sin activar; actívala desde el historial para aplicarla.'
    );
    setEditingPromptSection(null);
  };

  const saveBusinessUnitSettings = async (businessUnitUpdate: BusinessUnitUpdate, successMessage: string) => {
    if (!businessUnit) return;
    try {
      await updateBusinessUnit({ id: businessUnit.id, businessUnit: businessUnitUpdate }).unwrap();
      notifySaved(successMessage);
    } catch (updateError) {
      console.error(updateError);
    }
  };

  const handleUpdateLeadOrigins = () => {
    if (!businessUnit) return;
    const leadOriginGroups: Record<string, string[]> = {
      'Sitio Web - Whatsapp': websiteWhatsAppMessages,
      'Sitio Web - Formulario': websiteFormMessages,
      Mailing: mailingMessages,
      TikTok: tiktokMessages,
    };
    const seen = new Map<string, { original: string; groups: Set<string> }>();
    Object.entries(leadOriginGroups).forEach(([group, list]) => {
      list.forEach((raw) => {
        const key = raw.trim().toLowerCase();
        if (!key) return;
        const entry = seen.get(key) ?? { original: raw, groups: new Set<string>() };
        entry.groups.add(group);
        seen.set(key, entry);
      });
    });
    const overlaps = Array.from(seen.values()).filter((e) => e.groups.size > 1);
    if (overlaps.length) {
      const msg = overlaps.map((o) => `- "${o.original}" presente en: ${Array.from(o.groups).join(', ')}`).join('\n');
      window.alert(`Los siguientes patrones están en más de un grupo de origen:\n${msg}`);
      return;
    }
    void saveBusinessUnitSettings(
      {
        websiteWhatsAppMessages: websiteWhatsAppMessages.length ? websiteWhatsAppMessages : null,
        websiteFormMessages: websiteFormMessages.length ? websiteFormMessages : null,
        mailingMessages: mailingMessages.length ? mailingMessages : null,
        tiktokMessages: tiktokMessages.length ? tiktokMessages : null,
      },
      'Orígenes actualizados'
    );
  };

  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const handleBack = () => {
    if (
      isPromptVersionDirty &&
      !window.confirm('Tienes cambios sin guardar en el prompt o las tools. ¿Quieres descartarlos y salir?')
    ) {
      return;
    }
    navigate(-1);
  };

  const handleDeleteImage = (fileName: string) => {
    if (businessUnit) {
      deleteBusinessUnitImage({ businessUnitId: businessUnit.id, fileName })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const handleUploadImages = () => {
    if (imagesToUpload && businessUnit) {
      uploadBusinessUnitImages({ businessUnitId: businessUnit.id, files: imagesToUpload })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const validateFileName = (fileName: string): boolean => {
    // Check for special characters, spaces, or accented characters
    const validPattern = /^[a-z0-9_.-]+$/i;
    return validPattern.test(fileName);
  };

  const getImagesError = () => {
    if (!imagesToUpload?.length) {
      return 'No hay imágenes seleccionadas';
    }
    if (imagesToUpload.some((file) => file.size > 300_000)) {
      return 'Hay imágenes que pesan más de 300kb';
    }
    const invalidFiles = imagesToUpload.filter((file) => !validateFileName(file.name));
    if (invalidFiles.length > 0) {
      return `Los siguientes archivos tienen nombres inválidos: ${invalidFiles
        .map((f) => f.name)
        .join(', ')}. Usa solo letras, números, guiones bajos y puntos. Sin tildes, espacios ni caracteres especiales.`;
    }
    return null;
  };

  const handleDeleteVideo = (fileName: string) => {
    if (businessUnit) {
      deleteBusinessUnitVideo({ businessUnitId: businessUnit.id, fileName })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const handleUploadVideos = () => {
    if (videosToUpload && businessUnit) {
      uploadBusinessUnitVideos({ businessUnitId: businessUnit.id, files: videosToUpload })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const getVideosError = () => {
    if (!videosToUpload?.length) {
      return 'No hay videos seleccionados';
    }
    if (videosToUpload.some((file) => file.size > 10_000_000)) {
      return 'Hay videos que pesan más de 10MB';
    }
    const invalidFiles = videosToUpload.filter((file) => !validateFileName(file.name));
    if (invalidFiles.length > 0) {
      return `Los siguientes archivos tienen nombres inválidos: ${invalidFiles
        .map((f) => f.name)
        .join(', ')}. Usa solo letras, números, guiones bajos y puntos. Sin tildes, espacios ni caracteres especiales.`;
    }
    return null;
  };

  const handleDeleteDocument = (fileName: string) => {
    if (businessUnit) {
      deleteBusinessUnitDocument({ businessUnitId: businessUnit.id, fileName })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const handleUploadDocuments = () => {
    if (documentsToUpload && businessUnit) {
      uploadBusinessUnitDocuments({ businessUnitId: businessUnit.id, files: documentsToUpload })
        .then(() => {
          setRefetchFlag(!refetchFlag);
        })
        .catch((e) => console.error(e));
    }
  };

  const getDocumentsError = () => {
    if (!documentsToUpload?.length) {
      return 'No hay documentos seleccionados';
    }
    if (documentsToUpload.some((file) => file.size > 15_000_000)) {
      return 'Hay documentos que pesan más de 15MB';
    }
    const invalidFiles = documentsToUpload.filter((file) => !validateFileName(file.name));
    if (invalidFiles.length > 0) {
      return `Los siguientes archivos tienen nombres inválidos: ${invalidFiles
        .map((f) => f.name)
        .join(', ')}. Usa solo letras, números, guiones bajos y puntos. Sin tildes, espacios ni caracteres especiales.`;
    }
    return null;
  };

  useEffect(() => {
    if (businessUnit) {
      if (initializedPromptBusinessUnitIdRef.current !== businessUnit.id) {
        const initialPrompt = businessUnit.prompt ?? '';
        const initialFunctions = businessUnit.availableFunctions || [];
        setPrompt(initialPrompt);
        setFunctions(initialFunctions);
        setVersionBaselinePrompt(initialPrompt);
        setVersionBaselineFunctions(initialFunctions);
        setEditorParentVersionId(null);
        setVersionNote('');
        setVersionError(null);
        setSavedPromptVersion(null);
        setShowPromptVersionHistory(false);
        setCompareUnsavedPrompt(false);
        setIsEditingPrompt(false);
        setTagBuilderOpen(false);
        initializedPromptBusinessUnitIdRef.current = businessUnit.id;
      }
      setSelectedAIModel(businessUnit.aiModel || DEFAULT_AI_MODEL);
      setDaysToCloseLeads(businessUnit.daysToCloseLeads ?? null);
      setHasSellerManagement(businessUnit.hasSellerManagement ?? false);
      setHasClientQueueTableActive(businessUnit.hasClientQueueTableActive ?? false);
      setHasCallingEnabled(businessUnit.hasCallingEnabled ?? false);
      setUploadSimulationsToCRM(businessUnit.uploadSimulationsToCRM ?? false);
      setCustomReportInstructions(businessUnit.customReportInstructions ?? '');
      setReportSources(businessUnit.reportSources ?? []);
      setWebsiteWhatsAppMessages(businessUnit.websiteWhatsAppMessages ?? []);
      setWebsiteFormMessages(businessUnit.websiteFormMessages ?? []);
      setMailingMessages(businessUnit.mailingMessages ?? []);
      setTiktokMessages(businessUnit.tiktokMessages ?? []);
    }
  }, [businessUnit]);

  useEffect(() => {
    if (activePromptVersionId && (editorParentVersionId === null || !isPromptSnapshotDirty)) {
      setEditorParentVersionId(activePromptVersionId);
    }
  }, [activePromptVersionId, editorParentVersionId, isPromptSnapshotDirty]);

  useEffect(() => {
    if (isError) {
      // ctx.throw(400, msg) sends the message as the response body (a plain string), so prefer
      // `data` when present; fall back to JSON for unexpected error shapes.
      const data = (error as { data?: unknown })?.data;
      const message = typeof data === 'string' ? data : JSON.stringify(error);
      window.alert(`Error al actualizar la empresa: ${message}`);
    }
  }, [isError, error]);

  // Warn before losing unsaved prompt/tool version edits.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPromptVersionDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isPromptVersionDirty]);

  // BrowserRouter does not expose a navigation blocker. Capture in-app links so sidebar
  // navigation receives the same discard warning as the page's back action.
  useEffect(() => {
    if (!isPromptVersionDirty) return undefined;
    const handleLinkNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target;
      const anchor = target instanceof Element ? target.closest<HTMLAnchorElement>('a[href]') : null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.href === window.location.href) return;
      if (!window.confirm('Tienes cambios sin guardar en el prompt o las tools. ¿Quieres descartarlos y salir?')) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('click', handleLinkNavigation, true);
    return () => document.removeEventListener('click', handleLinkNavigation, true);
  }, [isPromptVersionDirty]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetchFlag toggles after saves/uploads to reload the business unit
  useEffect(() => {
    refetch().catch((err) => console.log(err));
  }, [refetchFlag, refetch]);

  const getStructuredPrompt = () => {
    if (!promptSections) return prompt;

    return prompt
      .split('\n')
      .map((line) => {
        const sectionMatch = line.match(/<SECTION:([^>]+)>/);
        if (sectionMatch) {
          const sectionName = sectionMatch[1];
          const section = promptSections.find(
            (s: PromptSection) => s.name === sectionName && s.isLastVersion && !s.isSuggestion
          );
          if (section) {
            return section.value;
          }
        }
        return line;
      })
      .join('\n');
  };

  const getStructuredPromptWithUI = () => {
    if (!promptSections) return prompt;

    return prompt
      .split('\n')
      .map((line) => {
        const sectionMatch = line.match(/<SECTION:([^>]+)>/);
        if (sectionMatch) {
          const sectionName = sectionMatch[1];
          const section = promptSections.find(
            (s: PromptSection) => s.name === sectionName && s.isLastVersion && !s.isSuggestion
          );
          if (section) {
            return `<div class="${styles.promptSectionContainer}">
              <div class="${styles.promptSectionHeader}">
                <span>${section.name}: ${section.displayName}</span>
                <button class="${styles.copySectionButton}" data-section-value="${section.value.replace(
                  /"/g,
                  '&quot;'
                )}">
                  <svg style="width: 14px; height: 14px;" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
                  </svg>
                  <span class="${styles.copySectionText}">Copiar Prompt Section</span>
                </button>
              </div>
              <div class="${styles.promptSection}">${section.value}</div>
            </div>`;
          }
        }
        return line;
      })
      .join('\n');
  };

  useEffect(() => {
    const copyButtons = document.querySelectorAll(`.${styles.copySectionButton}`);
    copyButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const value = button.getAttribute('data-section-value');
        if (value) {
          navigator.clipboard.writeText(value).catch(console.error);
        }
      });
    });
  }, []);

  if (!businessUnit || !isAdmin) {
    return <Typography variant="h3">No se encontró la empresa</Typography>;
  }

  return (
    <Box
      sx={{
        bgcolor: SURFACE_CONTAINER_LOW,
        minHeight: '100vh',
        px: { xs: 2, lg: 4 },
        py: { xs: 2, lg: 3 },
        fontFamily: 'Jost, sans-serif',
        color: NAVY,
      }}
    >
      <BusinessUnitConfigHeader onBack={handleBack} />

      <SectionCard title="Información general">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
          <Typography variant="body1" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            <b>Nombre:</b> {businessUnit?.name}
          </Typography>
          <Typography variant="body1" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            <b>Teléfono:</b> {businessUnit?.phoneNumber}
          </Typography>
          <Typography variant="body1" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            <b>WhatsApp Tiers:</b>{' '}
            {businessUnit?.phoneNumberTier?.map((tier) => `${tier.split('T')[0]} ${tier.split(':').at(-1)}`).join(', ')}
          </Typography>
          <Typography variant="body1" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            <b>Modelo de IA:</b> {businessUnit?.aiModel || `Por defecto (${DEFAULT_AI_MODEL})`}
          </Typography>
        </Box>
      </SectionCard>
      <SectionCard
        title="Prompt"
        subtitle="Edita el prompt y guarda sus cambios junto con las tools como una versión auditable."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={activePromptVersionId ? `Activa #${activePromptVersionId}` : 'Configuración actual activa'}
                size="small"
                sx={{ bgcolor: alpha(STATUS_GOOD, 0.12), color: NAVY, fontWeight: 600 }}
              />
              <Chip
                label={
                  editorParentVersionId ? `Editando desde #${editorParentVersionId}` : 'Editando configuración actual'
                }
                size="small"
                sx={{
                  bgcolor: alpha(NAVY, 0.08),
                  color: NAVY,
                  fontWeight: 600,
                }}
              />
              {isPromptSnapshotDirty && (
                <Typography variant="caption" sx={{ color: alpha(NAVY, 0.58) }}>
                  Cambios sin guardar
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {isPromptSnapshotDirty && (
                <SecondaryButton
                  onClick={() => {
                    setCompareUnsavedPrompt(true);
                    setShowPromptVersionHistory(true);
                  }}
                  sx={{ py: 0.7 }}
                >
                  Ver diferencias
                </SecondaryButton>
              )}
              <SecondaryButton
                startIcon={<ListIcon />}
                onClick={() => {
                  setCompareUnsavedPrompt(false);
                  setShowPromptVersionHistory(true);
                }}
                disabled={!promptVersionsTotal}
                sx={{ py: 0.7 }}
              >
                Ver historial
              </SecondaryButton>
            </Box>
          </Box>
        </Box>
        <Box
          ref={promptContainerRef}
          sx={{ mt: 3 }}
          onClick={
            isEditingPrompt
              ? undefined
              : (event) => {
                  const tag = (event.target as HTMLElement).closest<HTMLElement>('[data-prompt-section-name]');
                  const sectionName = tag?.dataset.promptSectionName;
                  if (sectionName) openPromptSectionEditor(sectionName);
                }
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
              Contenido del prompt
            </Typography>
            {isEditingPrompt ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <PrimaryButton
                  onClick={openTagBuilder}
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
                  sx={{ py: 0.6, px: 2, fontSize: '0.85rem' }}
                >
                  Agregar tag
                </PrimaryButton>
                <SecondaryButton
                  onClick={openSectionPicker}
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
                  disabled={availablePromptSections.length === 0}
                  sx={{ py: 0.6, px: 2, fontSize: '0.85rem' }}
                >
                  Agregar sección
                </SecondaryButton>
                <Tooltip title="selecciona texto para crear sección" disableHoverListener={hasPromptSelection}>
                  <span>
                    <SecondaryButton
                      onClick={openSectionCreator}
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
                      disabled={!hasPromptSelection}
                      sx={{ py: 0.6, px: 2, fontSize: '0.85rem' }}
                    >
                      Crear sección
                    </SecondaryButton>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <PrimaryButton
                onClick={() => {
                  setHasPromptSelection(false);
                  setIsEditingPrompt(true);
                }}
                sx={{ py: 0.6, px: 2, fontSize: '0.85rem' }}
              >
                Editar
              </PrimaryButton>
            )}
          </Box>
          {/* One sizing box owns the height for both modes so the window stays identical
              across the preview ⇄ edit toggle; the inner content just fills it. */}
          <Box
            ref={attachPromptBox}
            style={{ height: `${promptHeightRef.current}px` }}
            sx={{
              width: '100%',
              minHeight: '200px',
              overflow: isEditingPrompt ? 'hidden' : 'auto',
              resize: 'vertical',
              p: isEditingPrompt ? 0 : 2,
              bgcolor: SURFACE_TINT,
              borderRadius: RADIUS_ITEM,
            }}
          >
            {isEditingPrompt ? (
              <PromptEditor
                editorRef={promptEditorRef}
                value={prompt}
                onChange={setPrompt}
                ariaLabel="Prompt principal de la unidad de negocio"
                promptSections={availablePromptSections}
                onSelectionChange={handlePromptSelectionChange}
                onPromptSectionClick={openPromptSectionEditor}
              />
            ) : prompt ? (
              promptPreview
            ) : (
              <Typography
                variant="body2"
                sx={{ color: 'rgba(21, 24, 58, 0.5)', fontFamily: 'Jost, sans-serif', fontStyle: 'italic' }}
              >
                Sin prompt configurado.
              </Typography>
            )}
          </Box>
        </Box>
        {businessUnitId && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
                  Secciones del prompt
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', mt: 0.5 }}>
                  Bloques reutilizables que el prompt referencia con &lt;SECTION:nombre&gt;.
                </Typography>
              </Box>
              <NewPromptSectionButton
                businessUnitId={Number(businessUnitId)}
                refetch={() => setRefetchPromptSectionsFlag(refetchPromptSectionsFlag + 1)}
              />
            </Box>
            <PromptSectionsForBusinessUnit
              businessUnitId={Number(businessUnitId)}
              refetchFlag={refetchPromptSectionsFlag}
            />
          </Box>
        )}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
            Tools disponibles de AI
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', mt: 0.5, mb: 2 }}>
            Busca y selecciona las tools que estarán disponibles para esta unidad de negocio.
          </Typography>
          <Autocomplete
            multiple
            options={availableFunctions}
            value={availableFunctions.filter((func) => functions.includes(func.function.name))}
            onChange={(_, newValue) => {
              setFunctions(newValue.map((func) => func.function.name));
            }}
            getOptionLabel={(option) => option.function.name}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  option.function.name.toLowerCase().includes(searchTerm) ||
                  option.function.description.toLowerCase().includes(searchTerm)
              );
            }}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar tools de AI"
                placeholder="Escribe para buscar por nombre o descripción..."
                variant="outlined"
              />
            )}
            renderValue={(value, getItemProps) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: '100px', overflowY: 'auto' }}>
                {value.map((option, index) => (
                  <Chip
                    {...getItemProps({ index })}
                    key={option.function.name}
                    label={option.function.name}
                    size="small"
                    sx={{
                      backgroundColor: '#C2EBF4',
                      color: NAVY,
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: NAVY,
                        '&:hover': { color: NAVY, opacity: 0.7 },
                      },
                    }}
                  />
                ))}
              </Box>
            )}
            renderOption={({ key: optionKey, ...props }, option, { selected }) => (
              <Box
                key={optionKey}
                component="li"
                {...props}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: SURFACE_HOVER,
                  },
                  backgroundColor: selected ? SURFACE_TINT : 'transparent',
                }}
              >
                <Checkbox
                  checked={selected}
                  sx={{
                    mr: 1.5,
                    color: 'rgba(21, 24, 58, 0.45)',
                    '&.Mui-checked': {
                      color: NAVY,
                    },
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontWeight: selected ? 600 : 500,
                      color: NAVY,
                      mb: 0.5,
                    }}
                  >
                    {option.function.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      color: 'rgba(21, 24, 58, 0.6)',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={option.function.description}
                  >
                    {option.function.description}
                  </Typography>
                </Box>
              </Box>
            )}
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                padding: '8px 12px',
              },
            }}
            noOptionsText="No se encontraron tools"
            loadingText="Cargando tools..."
            slots={{
              paper: AutocompletePaper,
            }}
          />
          {functions.length > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              {functions.length} tool{functions.length !== 1 ? 's' : ''} seleccionada
              {functions.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormField
            label="Descripción de cambios"
            optional
            value={versionNote}
            onChange={(event) => setVersionNote(event.target.value)}
          />
          {versionError && <InlineFeedback severity="error">{versionError}</InlineFeedback>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PrimaryButton
              onClick={() => void handleSavePromptVersion()}
              disabled={isSavingPromptVersion || !isPromptSnapshotDirty}
            >
              {isSavingPromptVersion ? 'Guardando…' : 'Guardar prompt y tools'}
            </PrimaryButton>
          </Box>
        </Box>
      </SectionCard>

      <SectionCard
        title="Instrucciones personalizadas para reportes"
        subtitle="Estas instrucciones se incluirán en los prompts de generación de reportes diarios/semanales/mensuales. Úsalo para dar contexto específico o indicaciones sobre cómo generar los reportes para esta unidad de negocio."
      >
        <TextField
          multiline
          rows={4}
          fullWidth
          value={customReportInstructions}
          onChange={(e) => setCustomReportInstructions(e.target.value)}
          placeholder="Ejemplo: Enfócate en vehículos de gama alta, prioriza análisis de clientes corporativos..."
          variant="outlined"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <SecondaryButton
            onClick={() =>
              void saveBusinessUnitSettings(
                { customReportInstructions: customReportInstructions || null },
                'Instrucciones actualizadas'
              )
            }
            disabled={isUpdatingBusinessUnit}
          >
            Guardar cambios
          </SecondaryButton>
        </Box>
      </SectionCard>

      <SectionCard
        title="Reportes por origen"
        subtitle="Además del reporte global de la unidad de negocio, se generará automáticamente (diario, semanal y mensual) un reporte adicional acotado a cada origen seleccionado. Déjalo vacío para generar solo el reporte global."
      >
        <Autocomplete<string, true, false, true>
          multiple
          freeSolo
          options={reportSourceOptions ?? []}
          value={reportSources}
          onChange={(_, newValue) => setReportSources(newValue as string[])}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecciona uno o más orígenes"
              helperText="Se generará un reporte por cada origen seleccionado, en cada período."
            />
          )}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <SecondaryButton
            onClick={() =>
              void saveBusinessUnitSettings(
                { reportSources: reportSources.length ? reportSources : null },
                'Orígenes de reportes actualizados'
              )
            }
            disabled={isUpdatingBusinessUnit}
          >
            Guardar cambios
          </SecondaryButton>
        </Box>
      </SectionCard>

      <SectionCard title="Modelo y permisos">
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <FormControl sx={{ minWidth: 250, maxWidth: '100%' }}>
            <InputLabel>Modelo de IA</InputLabel>
            <Select
              label="Modelo de IA"
              value={selectedAIModel || DEFAULT_AI_MODEL}
              onChange={(e) => setSelectedAIModel(e.target.value)}
              autoWidth
              renderValue={(value) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span>{selectedAIModel ? value : `Por defecto (${value})`}</span>
                  <CostTierBadge model={value} />
                </span>
              )}
            >
              <MenuItem value={DEFAULT_AI_MODEL}>
                <em>Por defecto ({DEFAULT_AI_MODEL})</em>
              </MenuItem>
              {aiModelOptions}
              {/* A business unit can hold a model we no longer offer; keep it selectable so it
                  shows instead of rendering an out-of-range value. */}
              {isUnknownAIModel && (
                <MenuItem value={selectedAIModel} sx={{ display: 'flex', gap: 1.5 }}>
                  <span>{selectedAIModel}</span>
                  <em>(desconocido)</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: 'text.secondary',
              width: '100%',
            }}
          >
            Modelo seleccionado: <strong>{selectedAIModel}</strong>
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={hasCallingEnabled}
                onChange={(e) => setHasCallingEnabled(e.target.checked)}
                sx={navyCheckboxSx}
              />
            }
            label="Llamadas Habilitadas"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={uploadSimulationsToCRM}
                onChange={(e) => setUploadSimulationsToCRM(e.target.checked)}
                sx={navyCheckboxSx}
              />
            }
            label="Subir Simulaciones al CRM"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={hasSellerManagement}
                onChange={(e) => {
                  const next = e.target.checked;
                  setHasSellerManagement(next);
                  if (!next) setHasClientQueueTableActive(false);
                }}
                sx={navyCheckboxSx}
              />
            }
            label="Gestión de Vendedores"
          />
          <Tooltip
            title={!hasSellerManagement ? 'Requiere "Gestión de Vendedores" activa' : ''}
            disableHoverListener={hasSellerManagement}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasClientQueueTableActive && hasSellerManagement}
                  onChange={(e) => setHasClientQueueTableActive(e.target.checked)}
                  disabled={!hasSellerManagement}
                  sx={navyCheckboxSx}
                />
              }
              label="Cola de Clientes"
            />
          </Tooltip>
          {hasSellerManagement && (
            <TextField
              label="Días para cerrar leads"
              type="number"
              value={daysToCloseLeads ?? ''}
              onChange={(e) => {
                const { value } = e.target;
                setDaysToCloseLeads(value === '' ? null : Number.parseInt(value, 10));
              }}
              sx={{ minWidth: 250 }}
              helperText="Número de días para cerrar leads automáticamente. Dejar vacío para desactivar."
              slotProps={{
                htmlInput: { min: 1 },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <SecondaryButton
            onClick={() =>
              void saveBusinessUnitSettings(
                {
                  aiModel: selectedAIModel,
                  daysToCloseLeads,
                  hasSellerManagement,
                  hasClientQueueTableActive: hasSellerManagement && hasClientQueueTableActive,
                  hasCallingEnabled,
                  uploadSimulationsToCRM,
                },
                'Modelo actualizado'
              )
            }
            disabled={isUpdatingBusinessUnit}
          >
            Guardar cambios
          </SecondaryButton>
        </Box>
      </SectionCard>

      <SectionCard title="Demochat">
        <Typography variant="body1" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
          <a href={`/demochat/${businessUnit?.pusherIdentification}`} target="_blank" rel="noreferrer">
            Abrir demochat
          </a>
        </Typography>
      </SectionCard>

      {isAdmin && (
        <DropdownCard
          label="Columnas del tablero de leads"
          open={showColumns}
          onChange={() => setShowColumns(!showColumns)}
        >
          <StatusConfigSection
            businessUnitId={businessUnit.id}
            statusConfig={businessUnit.statusConfig}
            onUpdate={() => void refetch()}
          />
        </DropdownCard>
      )}

      <DropdownCard
        label="Reglas de origen de leads"
        subtitle="Mensajes iniciales de WhatsApp que identifican el origen del lead. Se usan para clasificar leads entrantes por su mensaje inicial. Presiona Enter tras cada patrón."
        open={showLeadOrigin}
        onChange={() => setShowLeadOrigin(!showLeadOrigin)}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Sitio Web - Whatsapp
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={[] as string[]}
            value={websiteWhatsAppMessages}
            onChange={(_, newValue) => setWebsiteWhatsAppMessages(newValue as string[])}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ej: Hola, quiero cotizar"
                helperText="Si el mensaje incluye alguno de estos patrones, el origen será 'Sitio Web - Whatsapp'."
              />
            )}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Sitio Web - Formulario
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={[] as string[]}
            value={websiteFormMessages}
            onChange={(_, newValue) => setWebsiteFormMessages(newValue as string[])}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ej: Completé el formulario en la web"
                helperText="Si el mensaje incluye alguno de estos patrones, el origen será 'Sitio Web - Formulario'."
              />
            )}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Mailing
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={[] as string[]}
            value={mailingMessages}
            onChange={(_, newValue) => setMailingMessages(newValue as string[])}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ej: Vi el mail de..."
                helperText="Si el mensaje incluye alguno de estos patrones, el origen será 'Mailing'."
              />
            )}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            TikTok
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            options={[] as string[]}
            value={tiktokMessages}
            onChange={(_, newValue) => setTiktokMessages(newValue as string[])}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => <Chip {...getItemProps({ index })} key={option} label={option} />)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ej: Vi tu TikTok"
                helperText="Si el mensaje incluye alguno de estos patrones, el origen será 'TikTok'."
              />
            )}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <SecondaryButton onClick={handleUpdateLeadOrigins} disabled={isUpdatingBusinessUnit}>
            Guardar cambios
          </SecondaryButton>
        </Box>
      </DropdownCard>

      <DropdownCard label="Feriados" open={showHolidays} onChange={() => setShowHolidays(!showHolidays)}>
        <HolidaysSection businessUnitId={businessUnit.id} />
      </DropdownCard>

      <DropdownCard label="Alertas" open={showAlerts} onChange={() => setShowAlerts(!showAlerts)}>
        <BusinessUnitAlerts businessUnit={businessUnit} />
      </DropdownCard>

      <DropdownCard
        label="Casilla de email (Gmail)"
        open={showEmailChannel}
        onChange={() => setShowEmailChannel(!showEmailChannel)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
          <Typography variant="body2" sx={{ color: NAVY }}>
            {businessUnit.emailProperties?.address
              ? `Conectada: ${businessUnit.emailProperties.address}`
              : 'Sin casilla conectada. Conecta una cuenta de Gmail para recibir y responder correos de clientes.'}
          </Typography>
          <PrimaryButton
            disabled={isConnectingEmail}
            onClick={async () => {
              const url = await triggerEmailConnectUrl(businessUnit.id).unwrap();
              window.location.href = url;
            }}
          >
            {businessUnit.emailProperties?.address ? 'Reconectar casilla' : 'Conectar casilla'}
          </PrimaryButton>
        </Box>
      </DropdownCard>

      <SmtpEmailCard
        businessUnitId={businessUnit.id}
        emailProperties={businessUnit.emailProperties}
        onSaved={() => refetch()}
      />

      <DropdownCard label="Plantillas" open={showTemplates} onChange={() => setShowTemplates(!showTemplates)}>
        {businessUnitId && <BusinessUnitTemplates businessUnitId={Number(businessUnitId)} />}
      </DropdownCard>

      <DropdownCard label="Videos" open={showVideos} onChange={() => setShowVideos(!showVideos)}>
        <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif', mb: 2 }}>
          Tip: Nombra tus videos de forma descriptiva (Ej: ford_mustang_video_interior.mp4). Sin tildes, espacios ni
          caracteres especiales.
        </Typography>
        {businessUnit?.videos &&
          Object.keys(businessUnit.videos).map((key) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <a
                href={businessUnit.videos?.[key]?.publicFileURL}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: NAVY }}
              >
                {key}
              </a>
              <IconButton onClick={() => handleDeleteVideo(key)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <SecondaryButton component="label">
            Seleccionar videos
            <input
              hidden
              type="file"
              accept=".mp4,.3gp"
              multiple
              onChange={(e) => {
                if (e.target.files?.length) {
                  setVideosToUpload(Array.from(e.target.files));
                } else if (e.target.files && !e.target.files.length) {
                  setVideosToUpload(null);
                }
              }}
            />
          </SecondaryButton>
          {videosToUpload?.length ? (
            <Typography variant="body2" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
              {videosToUpload.length} archivo{videosToUpload.length !== 1 ? 's' : ''} seleccionado
              {videosToUpload.length !== 1 ? 's' : ''}
            </Typography>
          ) : null}
          <PrimaryButton onClick={handleUploadVideos} disabled={!!getVideosError()}>
            Subir videos
          </PrimaryButton>
        </Box>
        {getVideosError() && (
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif' }}>
            {getVideosError()}
          </Typography>
        )}
      </DropdownCard>

      <DropdownCard label="Imágenes" open={showImages} onChange={() => setShowImages(!showImages)}>
        <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif', mb: 2 }}>
          Tip: Nombra tus imágenes de forma descriptiva (Ej: ford_mustang_foto_delantera.jpg). Sin tildes, espacios ni
          caracteres especiales.
        </Typography>
        {businessUnit?.images &&
          Object.keys(businessUnit.images).map((key) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <a
                href={businessUnit.images?.[key]?.publicFileURL}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: NAVY }}
              >
                {key}
              </a>
              <IconButton onClick={() => handleDeleteImage(key)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <SecondaryButton component="label">
            Seleccionar imágenes
            <input
              hidden
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={(e) => {
                if (e.target.files?.length) {
                  setImagesToUpload(Array.from(e.target.files));
                } else if (e.target.files && !e.target.files.length) {
                  setImagesToUpload(null);
                }
              }}
            />
          </SecondaryButton>
          {imagesToUpload?.length ? (
            <Typography variant="body2" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
              {imagesToUpload.length} archivo{imagesToUpload.length !== 1 ? 's' : ''} seleccionado
              {imagesToUpload.length !== 1 ? 's' : ''}
            </Typography>
          ) : null}
          <PrimaryButton onClick={handleUploadImages} disabled={!!getImagesError()}>
            Subir imágenes
          </PrimaryButton>
        </Box>
        {getImagesError() && (
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif' }}>
            {getImagesError()}
          </Typography>
        )}
      </DropdownCard>

      <DropdownCard label="Documentos" open={showDocuments} onChange={() => setShowDocuments(!showDocuments)}>
        <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif', mb: 2 }}>
          Tip: Nombra tus documentos de forma descriptiva (Ej: catalogo_productos.pdf). Sin tildes, espacios ni
          caracteres especiales.
        </Typography>
        {businessUnit?.documents &&
          Object.keys(businessUnit.documents).map((key) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <a
                href={businessUnit.documents?.[key]?.publicFileURL}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: NAVY }}
              >
                {key}
              </a>
              <IconButton onClick={() => handleDeleteDocument(key)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <SecondaryButton component="label">
            Seleccionar documentos
            <input
              hidden
              type="file"
              accept=".pdf"
              multiple
              onChange={(e) => {
                if (e.target.files?.length) {
                  setDocumentsToUpload(Array.from(e.target.files));
                } else if (e.target.files && !e.target.files.length) {
                  setDocumentsToUpload(null);
                }
              }}
            />
          </SecondaryButton>
          {documentsToUpload?.length ? (
            <Typography variant="body2" sx={{ color: NAVY, fontFamily: 'Jost, sans-serif' }}>
              {documentsToUpload.length} archivo{documentsToUpload.length !== 1 ? 's' : ''} seleccionado
              {documentsToUpload.length !== 1 ? 's' : ''}
            </Typography>
          ) : null}
          <PrimaryButton onClick={handleUploadDocuments} disabled={!!getDocumentsError()}>
            Subir documentos
          </PrimaryButton>
        </Box>
        {getDocumentsError() && (
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(21, 24, 58, 0.6)', fontFamily: 'Jost, sans-serif' }}>
            {getDocumentsError()}
          </Typography>
        )}
      </DropdownCard>

      <DropdownCard label="Tópicos de conversación" open={showTopics} onChange={() => setShowTopics(!showTopics)}>
        <TopicsConfigSection
          conversationTaggingConfig={businessUnit.conversationTaggingConfig}
          onSave={(conversationTaggingConfig) =>
            updateBusinessUnit({ id: businessUnit.id, businessUnit: { conversationTaggingConfig } }).then(() =>
              refetch()
            )
          }
        />
      </DropdownCard>

      <DropdownCard
        label="Instrucciones"
        open={showInstructions}
        onChange={() => setShowInstructions(!showInstructions)}
      >
        {PROMPT_HELP_TEXT}
      </DropdownCard>

      <DropdownCard
        label="Prompt estructurado"
        open={showStructuredPrompt}
        onChange={() => setShowStructuredPrompt(!showStructuredPrompt)}
      >
        {promptSections && prompt && (
          <Box>
            <Box
              sx={{
                bgcolor: SURFACE_TINT,
                borderRadius: `${RADIUS_ITEM} ${RADIUS_ITEM} 0 0`,
                p: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(getStructuredPrompt()).catch(console.error);
                }}
                title="Copiar prompt completo"
                size="small"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: NAVY }}
              >
                <ContentCopyIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif' }}>
                  Copiar Prompt
                </Typography>
              </IconButton>
            </Box>
            <Box
              className={styles.structuredPromptContainer}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: rendering structured prompt HTML from internal source
              dangerouslySetInnerHTML={{ __html: getStructuredPromptWithUI() }}
            />
          </Box>
        )}
      </DropdownCard>

      <DropdownCard
        label="Business unit ads"
        open={showBusinessUnitAds}
        onChange={() => setShowBusinessUnitAds(!showBusinessUnitAds)}
      >
        {businessUnitId && businessUnit && <BusinessUnitAds businessUnitId={Number(businessUnitId)} />}
      </DropdownCard>

      <ActivateSavedPromptVersionDialog
        key={savedPromptVersion?.id ?? 'no-saved-version'}
        businessUnitId={numericBusinessUnitId}
        version={savedPromptVersion}
        expectedActiveVersionId={activePromptVersionId}
        onClose={() => setSavedPromptVersion(null)}
        onActivated={(version) => {
          setSavedPromptVersion(null);
          applyVersionToEditor(version);
          refetchPromptVersions().catch(console.error);
          refetch().catch(console.error);
        }}
      />
      <PromptVersionHistoryDialog
        open={showPromptVersionHistory}
        businessUnitId={numericBusinessUnitId}
        activeVersionId={activePromptVersionId}
        isDirty={isPromptVersionDirty}
        editorSnapshot={
          compareUnsavedPrompt
            ? {
                prompt,
                availableFunctions: normalizedFunctions,
                parentVersionId: editorParentVersionId,
                sectionSnapshot: null,
              }
            : null
        }
        fallbackBaseSnapshot={{
          prompt: versionBaselinePrompt,
          availableFunctions: versionBaselineFunctions,
          sectionSnapshot: null,
        }}
        isSavingEditorSnapshot={isSavingPromptVersion}
        onClose={() => {
          setShowPromptVersionHistory(false);
          setCompareUnsavedPrompt(false);
        }}
        onSaveEditorSnapshot={() => {
          setShowPromptVersionHistory(false);
          setCompareUnsavedPrompt(false);
          void handleSavePromptVersion();
        }}
        onActivated={(version) => {
          applyVersionToEditor(version);
          refetch().catch(console.error);
        }}
        onRefetch={() => refetchPromptVersions()}
      />

      <PromptTagBuilderDialog
        open={tagBuilderOpen}
        onClose={() => setTagBuilderOpen(false)}
        onInsert={insertPromptTag}
        initialInnerText={tagInitialInnerRef.current}
      />
      <PromptSectionPickerDialog
        open={sectionPickerOpen}
        promptSections={availablePromptSections}
        onClose={() => setSectionPickerOpen(false)}
        onInsert={(tag) => {
          insertPromptTag(tag);
          setSectionPickerOpen(false);
        }}
      />
      {sectionCreatorOpen && businessUnitId && (
        <PromptSectionModal
          close={() => setSectionCreatorOpen(false)}
          title="Crear sección desde el prompt"
          save={createSectionFromPromptSelection}
          createsPromptVersion={false}
          promptSection={{
            businessUnitId: Number(businessUnitId),
            name: '',
            displayName: '',
            value: sectionCreatorValue,
            permission: 'editable',
          }}
        />
      )}
      {editingPromptSection && (
        <PromptSectionModal
          close={() => setEditingPromptSection(null)}
          title="Editar sección"
          save={updatePromptSectionFromEditor}
          createsPromptVersion={Boolean(editingPromptSection.isUsed)}
          promptSection={editingPromptSection}
        />
      )}
    </Box>
  );
};

export default BusinessUnits;
